import React, {useEffect, useMemo} from "react";
import {Accordion, AccordionDetails, AccordionSummary, Box, Grid, Tooltip, Typography, useTheme} from "@mui/material";
import {ExpandMore} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {useFormContext, useWatch} from "react-hook-form";
import {useRecoilState, useRecoilValue} from "recoil";
import _ from "underscore";
import {ICSAncillary, IRADAncillary, IRaFillingForm} from "../../../models/models";
import {useQuery} from "@tanstack/react-query";
import {fetchRaDetail} from "../../../ra-detail/services/http.services";
import {bookingDataValueState, counterSaleManagementValueState, raFillingAccordionValidationState} from "../../state";
import {FormCheckbox, FormNumberInput, FormTextField} from "../../../common/form-fields-wrappers";
import {formatCurrency, getLocaleByLang} from "../../../utils";
import {deepClone} from "../../../../../util/state/commonFunctions";
import {LANGUAGES, LOCALES} from "../../../config";
import {AncillarySubtype, SEGMENT_VALUES} from "../config";
import {isCoverageDisabledByMutualExclusion, isMutuallyExclusiveCoverage, getEffectiveSubtypes} from "../../../../shared/coverageMutualExclusion";
import AncillaryDiscountSelect from "./AncillaryDiscountSelect";
import {isDiscountApplicable} from "./discount";
import {IAncillaryDiscountRange} from "./discount";
import {IIndexedAncillary, partitionChangeGroup} from "./changeGroup";
import ChangeGroupMultiSelect, {IChangeGroupOption} from "./ChangeGroupMultiSelect";
import useDeskDiscountRange from "./useDeskDiscountRange";

const AncillariesInfo = () => {
	const theme = useTheme();
	const {t, i18n} = useTranslation(['network']);
	const counterSaleManagement = useRecoilValue(counterSaleManagementValueState);
	const [raFillingAccordionValidation, setRaFillingAccordionValidation] = useRecoilState(raFillingAccordionValidationState);
	const deskDiscountRange = useDeskDiscountRange();

	const raFillingDataForm = useFormContext<IRaFillingForm>();

	const ancillariesFormValues = raFillingDataForm.getValues('ancillariesInfo');

	const ancillaryName = useMemo((): 'ancillaryNameIt' | 'ancillaryNameEn' => {
		let prop = 'ancillaryDefaultLabel';
		LANGUAGES.every((elem: string) => {
			if (i18n.language.includes(elem)) {
				prop = 'ancillaryName' + elem.charAt(0).toUpperCase() + elem.slice(1);
				return false;
			}
			return true;
		});

		return prop as 'ancillaryNameIt' | 'ancillaryNameEn';
	}, [i18n.language]);

	const handleAccordionOnChange = (e: React.SyntheticEvent, expanded: boolean) => {
		e.stopPropagation();
		
		setRaFillingAccordionValidation((prev) => {
			return {...prev, ["ancillariesInfo"]: expanded};
		})
	}

	return (
		<>
			<Accordion expanded={raFillingAccordionValidation["ancillariesInfo"]} onChange={(e, expanded) => handleAccordionOnChange(e, expanded)}>
				<AccordionSummary expandIcon={<ExpandMore />}>
					<Typography variant={"h6"}>
						{t('raFilling.ancillariesInfo.accordion.title')}
					</Typography>
				</AccordionSummary>

				<AccordionDetails>
					{
						!!counterSaleManagement?.last?.bookedAncillaryList?.length &&
						<Grid container columnSpacing={2} sx={{mb: theme.spacing(4)}}>
							<Grid item xs={12}>
								<Typography variant={"bodyLarge"}>
									{t("raFilling.ancillariesInfo.section.bookedAncillaries")}
								</Typography>
							</Grid>

							{
								// LPP-22293: partiziona le ancillary Change Group (^CG\d+$) e le renderizza
								// come singolo controllo multi-scelta; le altre restano box separati invariati.
								(() => {
									const {changeGroup, others} = partitionChangeGroup(ancillariesFormValues?.booked ?? []);
									return (
										<>
											{others.map(({ancillary, index}: IIndexedAncillary) =>
												<AncillaryField key={`${ancillary.ancillaryCode}`} elem={ancillary} type={'booked'} index={index} ancillaryName={ancillaryName} discountRange={deskDiscountRange} />
											)}
											{changeGroup.length > 0 &&
												<ChangeGroupField changeGroup={changeGroup} type={'booked'} ancillaryName={ancillaryName} discountRange={deskDiscountRange} />
											}
										</>
									);
								})()
							}

							<Grid item xs={12} sx={{pt: theme.spacing(2), display: "flex", justifyContent: "space-between"}}>
								<Typography variant={"body"} component={"span"}>
									{t("raFilling.ancillariesInfo.amount.booked")}
								</Typography>

								<Typography variant={"bodyLarge"} component={"span"} sx={{mr: theme.spacing(2)}}>
									<AncillaryTotalCostInfo
										amount={counterSaleManagement?.last?.bookedAncillaryAmount?.amountVatIncluded}
										promoAmount={counterSaleManagement?.last?.bookedAncillaryAmount?.promoAmountVatIncluded}
										currency={counterSaleManagement?.last?.currency}
										discountamount={null}
									/>
								</Typography>
							</Grid>
						</Grid>
					}

					<InheritedAncillaries ancillaryName={ancillaryName} />

					{/* Added Ancillaries */}
					{
						!!counterSaleManagement?.last?.counterSaleAncillaryList?.length &&
						<Grid container columnSpacing={2} sx={{mb: theme.spacing(4)}}>
							<Grid item xs={12}>
								<Typography variant={"bodyLarge"}>
									{t("raFilling.ancillariesInfo.section.addedAncillaries")}
								</Typography>
							</Grid>

							{
								// LPP-22293: stesso accorpamento sulle ancillary di banco (counterSale).
								(() => {
									const {changeGroup, others} = partitionChangeGroup(ancillariesFormValues?.counterSale ?? []);
									return (
										<>
											{others.map(({ancillary, index}: IIndexedAncillary) =>
												<AncillaryField key={`${ancillary.ancillaryCode}`} elem={ancillary} type={'counterSale'} index={index} ancillaryName={ancillaryName} discountRange={deskDiscountRange} />
											)}
											{changeGroup.length > 0 &&
												<ChangeGroupField changeGroup={changeGroup} type={'counterSale'} ancillaryName={ancillaryName} discountRange={deskDiscountRange} />
											}
										</>
									);
								})()
							}

							<Grid item xs={12} sx={{pt: theme.spacing(2), display: "flex", justifyContent: "space-between"}}>
								<Typography variant={"body"} component={"span"}>
									{t("raFilling.ancillariesInfo.amount.added")}
								</Typography>
								<Typography variant={"body"} component={"span"} sx={{mr: theme.spacing(2)}}>
									<AncillaryTotalCostInfo
										amount={counterSaleManagement?.last?.counterSaleAncillaryAmount?.amountVatIncluded}
										promoAmount={counterSaleManagement?.last?.counterSaleAncillaryAmount?.promoAmountVatIncluded}
										currency={counterSaleManagement?.last?.currency}
										discountamount={null}
									/>
								</Typography>
							</Grid>
						</Grid>
					}

					{/* Total Amount */}
					{
						!!counterSaleManagement?.last &&
						<Grid container columnSpacing={2} sx={{pt: theme.spacing(2)}}>
							<Grid item xs={12} sx={{display: "flex", justifyContent: "space-between"}}>
								<Typography variant={"bodyLarge"} component={"span"}>
									{t("raFilling.ancillariesInfo.amount.total")}
								</Typography>

								<Typography variant={"bodyLarge"} component={"span"} sx={{mr: theme.spacing(2)}}>
									<AncillaryTotalCostInfo
										amount={counterSaleManagement?.last?.totalAncillaryAmount?.amountVatIncluded}
										promoAmount={counterSaleManagement?.last?.totalAncillaryAmount?.promoAmountVatIncluded}
										currency={counterSaleManagement?.last?.currency}
										fontWeight={"bodyLarge"}
										discountamount={null}
									/>
								</Typography>
							</Grid>
						</Grid>
					}
				</AccordionDetails>
			</Accordion>
		</>
	);
}

export default AncillariesInfo;

// LPP-25233: subtype delle coperture che in sostituzione NTW esclude dal counter-sale (VB-02, LPP-21950 P1).
const INHERITED_COVERAGE_SUBTYPES = ['SUPER_COVER', 'SCDW', 'STP', 'G_T'];

/**
 * Accessori del RA padre da mostrare come ereditati: le coperture non gia' presenti nelle liste del
 * counter-sale e gli altri accessori al banco del padre ancora attivi, che NTW riporta sul nuovo segmento
 * (routeParentAncillariesForSubstitution).
 */
export const selectInheritedAncillaries = (parentRa: any, counterSaleLast: any): IRADAncillary[] => {
	const inCounterSale = new Set([
		...(counterSaleLast?.bookedAncillaryList ?? []),
		...(counterSaleLast?.counterSaleAncillaryList ?? []),
	].map((a: ICSAncillary) => a.ancillaryCode));
	const isCoverage = (a: IRADAncillary) => INHERITED_COVERAGE_SUBTYPES.includes(a.ancillarySubType as string);
	const coverages = [
		...(parentRa?.ancillariesInfo?.ancillaries ?? []),
		...(parentRa?.ancillariesInfo?.ancillariesAdded ?? []),
	].filter((a: IRADAncillary) => isCoverage(a) && !inCounterSale.has(a.ancillaryCode));
	const others = (parentRa?.ancillariesInfo?.ancillariesAdded ?? [])
		.filter((a: IRADAncillary) => !isCoverage(a) && (Number(a.ancillaryUnits ?? 0) > 0 || Number(a.ancillaryMileage ?? 0) > 0));
	return [...coverages, ...others];
};

/**
 * LPP-25233: in sostituzione tutto quello che arriva dal RA padre si vede qui, con la stessa nota: le
 * coperture, che non passano dal counter-sale (NTW le copia sul nuovo segmento a fine emissione), e gli altri
 * accessori, che restano anche nei loro controlli qui sotto. Sola lettura, letta dal dettaglio del padre:
 * fuori dal form e dai totali, non arriva al payload counter-sale ne' alla fattura di emissione.
 */
const InheritedAncillaries = ({ancillaryName}: {ancillaryName: 'ancillaryNameIt' | 'ancillaryNameEn'}) => {
	const theme = useTheme();
	const {t} = useTranslation(['network']);
	const bookingDataValue = useRecoilValue(bookingDataValueState);
	const counterSaleManagement = useRecoilValue(counterSaleManagementValueState);
	const parentId: string | undefined = bookingDataValue?.bookingDetail?.flagSubstitution
		? bookingDataValue?.parentIdRentalAgreement : undefined;

	const {data: parentRa} = useQuery(['raDetail', {idRentalAgreement: parentId}],
		() => fetchRaDetail({idRentalAgreement: parentId}),
		{enabled: !!parentId, refetchOnWindowFocus: false});

	const inherited = selectInheritedAncillaries(parentRa, counterSaleManagement?.last);

	if (!inherited.length) return null;

	return (
		<Grid container columnSpacing={2} sx={{mb: theme.spacing(4)}}>
			<Grid item xs={12}>
				<Typography variant={"bodyLarge"}>
					{t("raFilling.ancillariesInfo.section.inheritedAncillaries")}
				</Typography>
			</Grid>
			{inherited.map((a: IRADAncillary) =>
				<Grid item xs={12} key={a.ancillaryCode} sx={{pt: theme.spacing(1)}}>
					<Typography variant={"body"} component={"span"}>
						{a[ancillaryName] ?? a.ancillaryDescr ?? a.ancillaryCode}
					</Typography>
				</Grid>
			)}
		</Grid>
	);
}

/**
 * LPP-22293 / US-NTWRA-005 (RDT §3 SCFE-1, §2.1) — contenitore che collega il controllo
 * multi-scelta Change Group al form e a `counterSaleManagement`. La selezione/deselezione
 * valorizza le stesse `ICSAncillary` sottostanti (units 1/0), mantenendo payload, prezzo e
 * totale invariati (Requirement 2, RF-07). Nessun nuovo endpoint/config: sola presentazione.
 */
const ChangeGroupField = ({changeGroup, type, ancillaryName, discountRange}: {changeGroup: IIndexedAncillary[], type: 'booked' | 'counterSale', ancillaryName: 'ancillaryNameIt' | 'ancillaryNameEn', discountRange?: IAncillaryDiscountRange | null}) => {
	const theme = useTheme();
	const {t} = useTranslation(['network']);
	const raFillingDataForm = useFormContext<IRaFillingForm>();
	const [counterSaleManagement, setCounterSaleManagement] = useRecoilState(counterSaleManagementValueState);
	const bookingDataValue = useRecoilValue(bookingDataValueState);

	const options: IChangeGroupOption[] = useMemo(
		() => changeGroup.map(({ancillary}) => ({
			code: ancillary.ancillaryCode,
			label: (ancillary[ancillaryName] as string) ?? ancillary.ancillaryCode,
			ancillary,
		})),
		[changeGroup, ancillaryName]
	);

	// selezione corrente derivata dalla lista reattiva in counterSaleManagement (fonte del payload)
	const list = type === 'booked'
		? counterSaleManagement?.last?.bookedAncillaryList
		: counterSaleManagement?.last?.counterSaleAncillaryList;
	const groupCodes = new Set(changeGroup.map(({ancillary}) => ancillary.ancillaryCode));
	const selectedCodes = (list ?? [])
		.filter((a: ICSAncillary) => groupCodes.has(a.ancillaryCode))
		.filter((a: ICSAncillary) => (Number(a.units ?? 0) > 0) || a.units === true)
		.map((a: ICSAncillary) => a.ancillaryCode);

	const handleChange = (nextSelected: string[]) => {
		const selected = new Set(nextSelected);

		// 1) aggiorna il form (units 1/0) sugli indici originali, coerente con AncillaryField
		changeGroup.forEach(({ancillary, index}) => {
			raFillingDataForm.setValue(
				`ancillariesInfo.${type}.${index}.units`,
				selected.has(ancillary.ancillaryCode) ? 1 : 0
			);
		});

		// 2) aggiorna counterSaleManagement (payload counter-sale) con le stesse entita'
		const csmClone = deepClone(counterSaleManagement.last);
		const targetList = type === 'booked' ? csmClone.bookedAncillaryList : csmClone.counterSaleAncillaryList;
		(targetList ?? []).forEach((ancillary: ICSAncillary) => {
			if (groupCodes.has(ancillary.ancillaryCode)) {
				ancillary.units = selected.has(ancillary.ancillaryCode) ? 1 : 0;
			}
		});
		setCounterSaleManagement({previous: counterSaleManagement.last, last: csmClone});
	};

	// Sconto % banco per il Change Group (LPP-22958): stesso wiring di AncillaryField — persiste la % sul
	// record ancillary in counterSaleManagement così transita nel payload deskDiscounts verso il BE.
	const storeDiscountValue = (percentage: number | null, ancillaryCode: string) => {
		const cs = deepClone(counterSaleManagement);
		const targetList = type === 'booked' ? cs.last.bookedAncillaryList : cs.last.counterSaleAncillaryList;
		for (let i = 0; i < (targetList ?? []).length; i++) {
			if (targetList[i].ancillaryCode === ancillaryCode) {
				targetList[i].discountPercentage = percentage;
				break;
			}
		}
		setCounterSaleManagement({previous: counterSaleManagement.last, last: cs.last});
	};

	// valore % vivo letto dalla lista counterSaleManagement (fonte del payload), così resta in sync dopo lo store
	const currentDiscount = (ancillaryCode: string): number | null => {
		const found = (list ?? []).find((a: ICSAncillary) => a.ancillaryCode === ancillaryCode);
		return (found?.discountPercentage ?? null) as number | null;
	};

	// LPP-21950 (AS-07): in sostituzione le ancillary ereditate dal padre (lista booked) non sono scontabili.
	const isInheritedInSubstitution = !!bookingDataValue.bookingDetail.flagSubstitution && type === 'booked';
	// VB-04 (LPP-23160): niente sconto banco sui Change Group prenotati online (origine ONLINE_PREPAID)
	// su RA subscription/retail prepagato. Allineato all'enforcement backend NTW.
	const isOnlinePrepaidBooked = type === 'booked' && (
		bookingDataValue.financialInfo?.segment === SEGMENT_VALUES.SUBSCRIPTION
		|| (bookingDataValue.financialInfo?.segment === SEGMENT_VALUES.RETAIL && bookingDataValue.financialInfo?.paymentTiming === '1'));

	return (
		<Grid container item xs={12} sx={{py: theme.spacing(1)}}>
			<Grid item xs={12}>
				<ChangeGroupMultiSelect
					testId={type}
					options={options}
					selectedCodes={selectedCodes}
					onChange={handleChange}
					disabled={bookingDataValue.bookingDetail.flagSubstitution && !changeGroup.some(({ancillary}) => ancillary.flagResellableInSubstitution)}
					renderPrice={(ancillary: ICSAncillary) => <AncillaryCostInfo elem={ancillary} />}
				/>
			</Grid>

			{
				// Sconto banco per ogni Change Group selezionato e scontabile (gap LPP-22293 → LPP-22958):
				// il multiselect è presentazionale, il controllo sconto vive qui nel container come in AncillaryField.
				!isInheritedInSubstitution && !isOnlinePrepaidBooked && changeGroup
					.filter(({ancillary}) => selectedCodes.includes(ancillary.ancillaryCode))
					.filter(({ancillary}) => isDiscountApplicable(ancillary))
					.map(({ancillary}) => (
						<Grid item xs={12} key={`cg_discount_${ancillary.ancillaryCode}`} sx={{display: "flex", justifyContent: "flex-end", pr: theme.spacing(2), pt: theme.spacing(1)}}>
							<AncillaryDiscountSelect
								testId={ancillary.ancillaryCode}
								value={currentDiscount(ancillary.ancillaryCode)}
								flagDiscountApplicable={ancillary.flagDiscountApplicable}
								range={discountRange}
								disabled={bookingDataValue.bookingDetail.flagSubstitution && !ancillary.flagResellableInSubstitution}
								onChange={(percentage) => storeDiscountValue(percentage, ancillary.ancillaryCode)}
							/>
						</Grid>
					))
			}
		</Grid>
	);
};

/**
 * Ancillary cost info - cost currency /unit
 */
const AncillaryCostInfo = ({elem}: {elem: ICSAncillary}) => {
	const theme = useTheme();
	const {t, i18n} = useTranslation(['network']);

	const locale = useMemo((): 'it-IT' | 'en-IN' | 'fr-FR' => {
		return getLocaleByLang(i18n.language);
	}, [i18n.language]);

	return (
		<>
			{
				(elem.promoPriceVatIncluded === null  || elem.promoPriceVatIncluded === undefined) ?
					<Typography variant={'body'} component={'span'} sx={{mx: theme.spacing(0.5)}}>
						{formatCurrency(locale as LOCALES, elem.priceVatIncluded, elem.currency as string)}{elem.mileageNeeded ? '/km' : elem.maxUnits > 1 ? '/u' : ''}
					</Typography> :
					<>
						<Typography variant={'bodySmall'} component={'span'}>
							(
							<span style={{textDecoration: 'line-through'}}>
								{formatCurrency(locale as LOCALES, elem.priceVatIncluded, elem.currency as string)}
							</span>
							)
						</Typography>
						<Typography variant={'body'} component={'span'} sx={{mx: theme.spacing(1)}}>
							{formatCurrency(locale as LOCALES, elem.promoPriceVatIncluded, elem.currency as string)}{elem.mileageNeeded ? '/km' : elem.maxUnits > 1 ? '/u' : ''}
						</Typography>
					</>
			}
		</>
	);
}

/**
 * Ancillary cost info - cost currency /unit
 */
export const AncillaryTotalCostInfo = ({amount, promoAmount, currency, fontWeight = 'body', discountamount}: {amount: number | null; promoAmount: number | null, currency: string, fontWeight?: 'body' | 'bodyLarge', discountamount: number | null;}) => {
	const theme = useTheme();
	const {i18n} = useTranslation(['network']);

	const locale = useMemo((): 'it-IT' | 'en-IN' | 'fr-FR' => {
		return getLocaleByLang(i18n.language);
	}, [i18n.language]);

	function calculateamount() {
		if (amount == null) {
			return amount;
		}

		if (discountamount == null) {
			return amount;
		}

		return amount - Math.abs(discountamount);
	}

	return (
		<>
			{
				(promoAmount === null || promoAmount === undefined) ?
					<Typography variant={'body'} component={'span'} sx={{mx: theme.spacing(0.5)}}>
						<strong>{formatCurrency(locale as LOCALES, calculateamount(), currency)}</strong>
					</Typography> :
					<>
						<Typography variant={'bodySmall'} component={'span'}>
							<strong>(
								<span style={{textDecoration: 'line-through'}}>
									{formatCurrency(locale as LOCALES, amount, currency)}
								</span>
							)</strong>
						</Typography>
						<Typography variant={fontWeight} component={'span'} sx={{mx: theme.spacing(1)}}>
							<strong>{formatCurrency(locale as LOCALES, promoAmount, currency)}</strong>
						</Typography>
					</>
			}
		</>
	);
}

// discountRange (CR LPP-22289) portato; ancillaryName resta a 2 lingue come sulla linea di
// certificazione: su release/main il tipo e' esteso a Fr/Es/Pt, campi che qui non esistono in ICSAncillary.
const AncillaryField = ({elem, type, index, ancillaryName, discountRange}: {elem: ICSAncillary, type: 'booked' | 'counterSale', index: number, ancillaryName: 'ancillaryNameIt' | 'ancillaryNameEn', discountRange?: IAncillaryDiscountRange | null}) => {
	const theme = useTheme();
	const raFillingDataForm = useFormContext<IRaFillingForm>();
	const [counterSaleManagement, setCounterSaleManagement] = useRecoilState(counterSaleManagementValueState);
	const bookingDataValue = useRecoilValue(bookingDataValueState);

	const fieldValue = useWatch<IRaFillingForm>({
		name: `ancillariesInfo.${type}.${index}.${elem.mileageNeeded ? 'mileage' : 'units'}`
	}) as number | undefined;

	// LPP-22290 / Requirement 5.1: mutua esclusione UI coperture SC/SCDW/STP. Si derivano i subtype
	// selezionati (units/mileage > 0) dalle liste booked+counterSale; se e' gia' attiva una copertura
	// le altre coperture vengono disabilitate (quella attiva resta selezionabile per deselezionarla).
	// LPP-21950: esplode i Pack per verificare le coperture contenute nei figli (packAncillaries).
	// Disabilita una candidata se un'ALTRA ancillary (ancillaryCode diverso) fornisce una copertura esclusiva.
	const allSelectedAncillaries = [
		...(counterSaleManagement?.last?.bookedAncillaryList ?? []),
		...(counterSaleManagement?.last?.counterSaleAncillaryList ?? []),
	].filter((a: ICSAncillary) => (Number(a.units ?? 0) > 0) || (Number(a.mileage ?? 0) > 0));

	const candidateSubtypes = getEffectiveSubtypes(elem);
	const candidateHasCoverage = candidateSubtypes.some((st: string) => isMutuallyExclusiveCoverage(st));
	const coverageDisabledByMutualExclusion = candidateHasCoverage && allSelectedAncillaries
		.filter((a: ICSAncillary) => a.ancillaryCode !== elem.ancillaryCode)
		.some((a: ICSAncillary) => getEffectiveSubtypes(a).some((st: string) => isMutuallyExclusiveCoverage(st)));

	const manageAncillary = async () => {
		const csmClone = deepClone(counterSaleManagement.last);
		const list = type === 'booked' ? 'bookedAncillaryList': 'counterSaleAncillaryList';
		if (_.isBoolean(fieldValue)) {
			csmClone[list]?.every((ancillary: ICSAncillary) => {
				if (ancillary.ancillaryCode === elem.ancillaryCode) {
					ancillary.units = fieldValue ? 1 : 0;
					return false;
				}

				return true;
			});
		} else {
			csmClone[list]?.every((ancillary: ICSAncillary) => {
				if (ancillary.ancillaryCode === elem.ancillaryCode) {
					if (ancillary.mileageNeeded) {
						ancillary.mileage = fieldValue as number;
					} else {
						ancillary.units = fieldValue as number;
					}
					return false;
				}
				return true;
			});
		}
		await setCounterSaleManagement({previous: counterSaleManagement.last, last: csmClone});
	}

	useEffect(() => {
		manageAncillary();
	}, [fieldValue])

	const storeTextValue = (event: React.SyntheticEvent, ancillary: ICSAncillary, type: 'booked' | 'counterSale') => {
		const cs = deepClone(counterSaleManagement);
		let list = [];
		if (type === 'booked') {
			list = cs.last.bookedAncillaryList;
		} else {
			list = cs.last.counterSaleAncillaryList;
		}

		for (let i = 0; i < list.length; i++) {
			if (list[i].ancillaryCode === ancillary.ancillaryCode) {
				// @ts-ignore
				list[i].address = event?.target?.value;
				break;
			}
		}

		setCounterSaleManagement({previous: counterSaleManagement.last, last: cs.last});
	}

	// Sconto % banco per-item (LPP-22958, RDT §2.1(11)): persiste la % scelta sul record
	// ancillary in counterSaleManagement, così transita nel payload verso il BE proxy.
	const storeDiscountValue = (percentage: number | null, ancillary: ICSAncillary, type: 'booked' | 'counterSale') => {
		const cs = deepClone(counterSaleManagement);
		const list = type === 'booked' ? cs.last.bookedAncillaryList : cs.last.counterSaleAncillaryList;

		for (let i = 0; i < list.length; i++) {
			if (list[i].ancillaryCode === ancillary.ancillaryCode) {
				list[i].discountPercentage = percentage;
				break;
			}
		}

		setCounterSaleManagement({previous: counterSaleManagement.last, last: cs.last});
	}

	// item selezionato: units/mileage valorizzati (numero>0) o checkbox true
	const isSelected = _.isBoolean(fieldValue) ? !!fieldValue : !!fieldValue && (fieldValue as number) > 0;
	// LPP-21950: in sostituzione le ancillary ereditate dal rent padre (lista booked) non sono scontabili:
	// arrivano col prezzo a cui sono state vendute, quindi una percentuale si sommerebbe a un eventuale
	// sconto già applicato sul padre (AS-07). Restano rimovibili (RF-02).
	const isInheritedInSubstitution = !!bookingDataValue.bookingDetail.flagSubstitution && type === 'booked';
	// VB-04 (LPP-23160): lo sconto al banco NON si applica agli accessori con origine ONLINE_PREPAID
	// (prenotati online su segmento prepagato). Gli accessori della lista "Prenotati" (booked) su un RA
	// subscription o retail prepagato sono ONLINE_PREPAID → niente tendina sconto. Gli "Aggiunti"
	// (counterSale, vendita al banco) restano scontabili. Allineato all'enforcement backend NTW.
	const isOnlinePrepaidBooked = type === 'booked' && (
		bookingDataValue.financialInfo?.segment === SEGMENT_VALUES.SUBSCRIPTION
		|| (bookingDataValue.financialInfo?.segment === SEGMENT_VALUES.RETAIL && bookingDataValue.financialInfo?.paymentTiming === '1'));
	// solo banco (VB-04): il flusso create-ra network è desk-only per costruzione, escluse le origini online-prepaid
	const showDiscount = isSelected && isDiscountApplicable(elem) && !isInheritedInSubstitution && !isOnlinePrepaidBooked;

	if (elem.maxUnits > 1 || elem.mileageNeeded) {

		return (
			<Grid container item xs={12} key={`${type}_${elem.ancillaryCode}`} sx={{py: theme.spacing(1), mb: 'auto'}}>
				<Grid item xs={8} sx={{display: "flex", alignItems: "flex-end", justifyContent: "flex-start"}}>
					<Box sx={{width: '100px'}}>
						<FormNumberInput<IRaFillingForm>
							name={`ancillariesInfo.${type}.${index}.${elem.mileageNeeded ? 'mileage' : 'units'}`}
							label={''}
							control={raFillingDataForm.control}
							minValue={0}
							maxValue={elem.mileageNeeded ? 999 : elem.maxUnits}
							disabled={(!elem.deselectable && !(bookingDataValue.bookingDetail.flagSubstitution && elem.flagResellableInSubstitution)) || (bookingDataValue.bookingDetail.flagSubstitution && !elem.flagResellableInSubstitution) || elem.subtype === AncillarySubtype.AdditionalDrivers || elem.ancillarySubType === AncillarySubtype.AdditionalDrivers || coverageDisabledByMutualExclusion}
						/>
					</Box>
					<Tooltip title={elem[ancillaryName] as string} disableInteractive sx={{pt: theme.spacing(2)}}>
						<Typography variant={'body'} sx={{ml: theme.spacing(2), py: theme.spacing(1)}}>
							{elem[ancillaryName]}
						</Typography>
					</Tooltip>
				</Grid>
				<Grid item xs={4} sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', py: theme.spacing(1), pr: theme.spacing(2)}}>
					<AncillaryCostInfo elem={elem}/>
				</Grid>

				{
					showDiscount &&
					<Grid item xs={12} sx={{display: "flex", justifyContent: "flex-end", pr: theme.spacing(2), pt: theme.spacing(1)}}>
						<AncillaryDiscountSelect
							testId={elem.ancillaryCode}
							value={(elem.discountPercentage ?? null) as number | null}
							flagDiscountApplicable={elem.flagDiscountApplicable}
							range={discountRange}
							disabled={bookingDataValue.bookingDetail.flagSubstitution && !elem.flagResellableInSubstitution}
							onChange={(percentage) => storeDiscountValue(percentage, elem, type)}
						/>
					</Grid>
				}

				{
					(['DELIVERY', 'COLLECTION'].includes(elem.ancillarySubType as string) || ['DELIVERY', 'COLLECTION'].includes(elem.subtype as string)) && !!fieldValue && fieldValue > 0 &&
					<Grid item xs={12} sx={{display: "flex", alignItems: "flex-end", justifyContent: "flex-start", pr: theme.spacing(2)}}>
						<FormTextField<IRaFillingForm>
							name={`ancillariesInfo.${type}.${index}.address`}
							label={elem[ancillaryName]}
							control={raFillingDataForm.control}
							required={!!fieldValue && fieldValue > 0}
							disabled={bookingDataValue.bookingDetail.flagSubstitution && !elem.flagResellableInSubstitution}
							onBlur={(event: React.SyntheticEvent) => storeTextValue(event, elem, type)}
						/>
					</Grid>
				}
			</Grid>
		)
	} else {
		return (
			<Grid container item xs={12} key={`${type}_${elem.ancillaryCode}`} sx={{py: theme.spacing(1)}}>
				<Grid item xs={8} sx={{display: "flex", alignItems: "flex-end", justifyContent: "flex-start", pt: theme.spacing(2)}}>
					<Tooltip title={elem.ancillaryDescription ?? ''} disableInteractive sx={{display: "flex", alignItems: "flex-end"}}>
						<span>
							<FormCheckbox<IRaFillingForm>
								name={`ancillariesInfo.${type}.${index}.units`}
								label={elem[ancillaryName] ?? elem.ancillaryDefaultLabel ?? ''}
								disabled={(!elem.deselectable && !(bookingDataValue.bookingDetail.flagSubstitution && elem.flagResellableInSubstitution)) || (bookingDataValue.bookingDetail.flagSubstitution && !elem.flagResellableInSubstitution) || coverageDisabledByMutualExclusion}
                                readOnly={elem.subtype === AncillarySubtype.AdditionalDrivers || elem.ancillarySubType === AncillarySubtype.AdditionalDrivers}
								// defaultValue={['A', 'Z'].includes(params.sellType) ? true : false}
								// disabled={['A', 'Z'].includes(params.sellType) || step === InsertBookingStep.CUSTOMER_DETAILS}
							/>
						</span>
					</Tooltip>
				</Grid>
				<Grid item xs={4} sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', py: theme.spacing(1), pr: theme.spacing(2)}}>
					<AncillaryCostInfo elem={elem}/>
				</Grid>

				{
					showDiscount &&
					<Grid item xs={12} sx={{display: "flex", justifyContent: "flex-end", pr: theme.spacing(2), pt: theme.spacing(1)}}>
						<AncillaryDiscountSelect
							testId={elem.ancillaryCode}
							value={(elem.discountPercentage ?? null) as number | null}
							flagDiscountApplicable={elem.flagDiscountApplicable}
							range={discountRange}
							disabled={bookingDataValue.bookingDetail.flagSubstitution && !elem.flagResellableInSubstitution}
							onChange={(percentage) => storeDiscountValue(percentage, elem, type)}
						/>
					</Grid>
				}

				{
					(['URBAN_DELIVERY', 'URBAN_COLLECTION'].includes(elem.ancillarySubType as string) || ['URBAN_DELIVERY', 'URBAN_COLLECTION'].includes(elem.subtype as string)) && !!fieldValue &&
					<Grid item xs={12} sx={{display: "flex", alignItems: "flex-end", justifyContent: "flex-start", pr: theme.spacing(2)}}>
						<FormTextField<IRaFillingForm>
							name={`ancillariesInfo.${type}.${index}.address`}
							label={elem[ancillaryName]}
							control={raFillingDataForm.control}
							required={!!fieldValue}
							disabled={bookingDataValue.bookingDetail.flagSubstitution && !elem.flagResellableInSubstitution}
							onBlur={(event: React.SyntheticEvent) => storeTextValue(event, elem, type)}
						/>
					</Grid>
				}
			</Grid>
		)
	}
}
